import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getStaffPerformance } from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
  Users,
  CheckCircle2,
  Clock,
  TrendingUp,
  Loader2,
  ClipboardList,
  DollarSign,
  Calendar,
  ChevronRight,
  BarChart3,
} from 'lucide-react';

const StaffPerformancePage = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadPerformance();
  }, []);

  const loadPerformance = async () => {
    try {
      const result = await getStaffPerformance();
      setData(result);
    } catch (error) {
      toast.error(t('failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  const getCompletionColor = (rate) => {
    if (rate >= 80) return 'text-green-600';
    if (rate >= 50) return 'text-amber-600';
    return 'text-red-600';
  };

  const getProgressColor = (rate) => {
    if (rate >= 80) return 'bg-green-500';
    if (rate >= 50) return 'bg-amber-500';
    return 'bg-red-500';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const { overall_stats, staff_performance } = data || { overall_stats: {}, staff_performance: [] };

  return (
    <div className="space-y-6" data-testid="staff-performance-page">
      {/* Header */}
      <div>
        <h1 className="text-4xl font-bold font-heading text-foreground">{t('staffPerformance')}</h1>
        <p className="text-muted-foreground mt-2">{t('staffPerformanceDesc')}</p>
      </div>

      {/* Overall Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <Users className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">{t('totalStaff')}</p>
                <p className="text-2xl font-bold">{overall_stats.total_staff || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-100">
                <ClipboardList className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">{t('totalTasks')}</p>
                <p className="text-2xl font-bold">{overall_stats.total_tasks || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-100">
                <CheckCircle2 className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">{t('completedTasks')}</p>
                <p className="text-2xl font-bold">{overall_stats.completed_tasks || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-100">
                <Clock className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">{t('pendingTasks')}</p>
                <p className="text-2xl font-bold">{overall_stats.pending_tasks || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-100">
                <TrendingUp className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">{t('completionRate')}</p>
                <p className="text-2xl font-bold">{overall_stats.overall_completion_rate || 0}%</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Staff Performance Cards */}
      <div className="space-y-4">
        <h2 className="text-xl font-semibold font-heading flex items-center gap-2">
          <BarChart3 className="h-5 w-5" />
          {t('individualPerformance')}
        </h2>
        
        {staff_performance.length > 0 ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {staff_performance.map((staff) => (
              <Card key={staff.staff_id} className="overflow-hidden" data-testid={`staff-card-${staff.staff_id}`}>
                <CardHeader className="pb-2 bg-muted/30">
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-lg font-heading">{staff.staff_name}</CardTitle>
                      <p className="text-sm text-muted-foreground">
                        {staff.position}
                        {staff.department && ` • ${staff.department}`}
                      </p>
                    </div>
                    <div className={`text-2xl font-bold ${getCompletionColor(staff.completion_rate)}`}>
                      {staff.completion_rate}%
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-4 space-y-4">
                  {/* Progress Bar */}
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-muted-foreground">{t('taskCompletion')}</span>
                      <span>{staff.completed_tasks}/{staff.total_tasks}</span>
                    </div>
                    <div className="h-2 bg-muted rounded-full overflow-hidden">
                      <div 
                        className={`h-full ${getProgressColor(staff.completion_rate)} transition-all`}
                        style={{ width: `${staff.completion_rate}%` }}
                      />
                    </div>
                  </div>

                  {/* Stats Grid */}
                  <div className="grid grid-cols-3 gap-3 text-center">
                    <div className="p-2 bg-muted/50 rounded-lg">
                      <p className="text-xs text-muted-foreground">{t('pending')}</p>
                      <p className="text-lg font-semibold text-amber-600">{staff.pending_tasks}</p>
                    </div>
                    <div className="p-2 bg-muted/50 rounded-lg">
                      <p className="text-xs text-muted-foreground">{t('avgDays')}</p>
                      <p className="text-lg font-semibold">
                        {staff.avg_completion_days !== null ? staff.avg_completion_days : '-'}
                      </p>
                    </div>
                    <div className="p-2 bg-muted/50 rounded-lg">
                      <p className="text-xs text-muted-foreground">{t('materialCost')}</p>
                      <p className="text-lg font-semibold">
                        ${staff.total_material_cost.toLocaleString()}
                      </p>
                    </div>
                  </div>

                  {/* Recent Tasks */}
                  {staff.recent_tasks.length > 0 && (
                    <div>
                      <p className="text-sm font-medium mb-2">{t('recentTasks')}</p>
                      <div className="space-y-1">
                        {staff.recent_tasks.slice(0, 3).map((task) => (
                          <div
                            key={task.id}
                            className="flex items-center justify-between text-sm p-2 rounded hover:bg-muted/50 cursor-pointer transition-colors"
                            onClick={() => navigate(`/work-orders/${task.id}`)}
                          >
                            <div className="flex items-center gap-2 min-w-0">
                              <span className="truncate">{task.title}</span>
                              <Badge variant="outline" className="text-xs shrink-0">
                                {task.farm}
                              </Badge>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              {task.task_confirmed ? (
                                <Badge className="bg-green-100 text-green-700 text-xs">
                                  {t('confirmed')}
                                </Badge>
                              ) : (
                                <Badge className="bg-amber-100 text-amber-700 text-xs">
                                  {t('pending')}
                                </Badge>
                              )}
                              <ChevronRight className="h-4 w-4 text-muted-foreground" />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="p-12">
              <div className="text-center text-muted-foreground">
                <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p className="text-lg mb-2">{t('noStaffData')}</p>
                <p className="text-sm">{t('noStaffDataDesc')}</p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default StaffPerformancePage;
